<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Under_the_Hood/Editor_Framework/External_Component_Box_Types';
    let nextLink= '/Documentation/Under_the_Hood/Editor_Interfaces';
    
</script>
<PrevNextSection {prevLink} {nextLink} />
<SectionComponent tag="h1" 
  id="the-freon-core-interfaces-1"
  bind:intersecting={visibleById["the-freon-core-interfaces-1"]}
  onIntersectChange={(visible) => setVisible("the-freon-core-interfaces-1", visible)}
>The Freon Core Interfaces</SectionComponent>
<SectionComponent tag="h2" 
  id="frenode-2"
  bind:intersecting={visibleById["frenode-2"]}
  onIntersectChange={(visible) => setVisible("frenode-2", visible)}
>FreNode</SectionComponent>
<p>As Freon uses the AST for projections and behavior, it needs to know about the type of elements in the AST.<br>
To allow Freon to work, each element type that occurs in the AST must implement the <code>FreNode</code> interface.<br>
This interface is kept as small as possible to allow Freon to be used for any AST.</p>
<pre class="language-ts"><code class="language-ts">/* File: core/src/ast/FreNode.ts */

export interface FreNode &#123;
	freId(): string;

	freLanguageConcept(): string;

	freOwner(): FreNode | undefined;

	freOwnerDescriptor(): FreOwnerDescriptor;

	freIsModel(): boolean;

	freIsUnit(): boolean;

	freIsExpression(): boolean;

	freIsBinaryExpression(): boolean;

	copy(): FreNode;

	match(toBeMatched: Partial&lt;FreNode&gt;): boolean;

	parseLocation?: FreParseLocation; // if relevant, the location of this node within the source from which it is parsed
&#125;</code></pre>
<p>The two most important members in this interface are:</p>
<ul>
<li><code>freId()</code> — returns a unique ID for each element in the AST.</li>
<li><code>freContainer()</code> — returns a descriptor for the container (parent) of an element in the AST.</li>
</ul>
<p>The functions <code>freIsExpression()</code> and <code>freIsBinaryExpression()</code> are only needed when your language
contains expressions, since Freon includes special handling for expression trees.<br>
To start with, these functions can simply return <code>false</code>.</p>
<p>Note that Freon does not need to know anything about the structure of your language:<br>
no available element types, no property names, and no model structure.<br>
This is intentional — Freon is not designed as a full <em>language workbench</em>,<br>
but as a flexible projectional editor that can integrate with multiple language workbenches (or none at all).</p>
<SectionComponent tag="h2" 
  id="frenamednode-3"
  bind:intersecting={visibleById["frenamednode-3"]}
  onIntersectChange={(visible) => setVisible("frenamednode-3", visible)}
>FreNamedNode</SectionComponent>
<p>An object that implements <code>FreNamedNode</code> can be a node in a model AST, similar to<br>
[<code>FreNode</code>] implementations.<br>
The difference is that <code>FreNamedNode</code> objects have a <code>name</code> property.</p>
<pre class="language-ts"><code class="language-ts">/* File: core/src/ast/FreNamedNode.ts */

export interface FreNamedNode extends FreNode &#123;
	name: string;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="freaction-4"
  bind:intersecting={visibleById["freaction-4"]}
  onIntersectChange={(visible) => setVisible("freaction-4", visible)}
>FreAction</SectionComponent>
<p><code>FreAction</code> is the abstract base class for all editor actions that can be triggered from boxes.</p>
<pre class="language-ts"><code class="language-ts">/* File: core/src/editor/actions/FreAction.ts */

export abstract class FreAction &#123;
	/**
	 * The trigger to activate this behavior
	 */
	trigger: FreTriggerType;

	/**
	 * The box roles in which this trigger is active
	 */
	activeInBoxRoles: string[];

	/**
	 * Optional callback function that returns whether the trigger is applicable for the specific box.
	 */
	isApplicable?: (box: Box) =&gt; boolean;

	/**
	 * The role of the box that should be selected after the action has been executing.
	 */
	boxRoleToSelect?: string;

	/**
	 * The caret position where the cursor should be positioned after the action has been executed.
	 * Only applicable if the selected box is a TextBox.
	 */
	caretPosition?: FreCaret;

	/**
	 * The property name of the reference for which this is a shortcut.
	 */
	referenceShortcut?: ReferenceShortcut;

	/**
	 * Execute the action
	 * @param box       The selected box on which this action is executed
	 * @param trigger   The trigger that causes this action to execute
	 * @param editor    The editor
	 * @param index     The index in the list, if there is any
	 */
	abstract execute(box: Box, trigger: FreTriggerUse, editor: FreEditor, index?: number): FrePostAction;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="frecustomaction-5"
  bind:intersecting={visibleById["frecustomaction-5"]}
  onIntersectChange={(visible) => setVisible("frecustomaction-5", visible)}
>FreCustomAction</SectionComponent>
<p>A concrete implementation of <code>FreAction</code>, <code>FreCustomAction</code> defines a customizable editor action
executed via a function stored in its <code>action</code> property.</p>
<pre class="language-ts"><code class="language-ts">/* File: core/src/editor/actions/FreCustomAction.ts */

import &#123; AST &#125; from "../../change-manager/index.js";
import &#123; FreUtils &#125; from "../../util/index.js";
import &#123; Box &#125; from "../boxes/index.js";
import &#123; FreEditor &#125; from "../FreEditor.js";
import &#123;
FreAction,
CustomAction,
FreTriggerUse,
FrePostAction,
triggerTypeToString,
ACTION_LOGGER
&#125; from "./internal.js";

export class FreCustomAction extends FreAction &#123;
	static create(initializer?: Partial&lt;FreCustomAction&gt;) &#123;
		const result = new FreCustomAction();
		FreUtils.initializeObject(result, initializer);
		return result;
	&#125;
	/**
	 * The action function that will be performed
	 */
	action: CustomAction;

	constructor() &#123;
		super();
	&#125;

	/**
	 * @see FreAction.execute
	 * @param box
	 * @param trigger
	 * @param editor
	 */
	override execute(box: Box, trigger: FreTriggerUse, editor: FreEditor): FrePostAction &#123;
		ACTION_LOGGER.log("FreCustomCommand: trigger [" + triggerTypeToString(trigger) + "]");
		ACTION_LOGGER.log("FreCustomCommand: action [" + this.action + "]");
		const self = this;
		let selected
		AST.change( () =&gt; &#123;
			selected = self.action(box, triggerTypeToString(trigger), editor);
		&#125;)
		if (!!selected) &#123;
			if (!!self.boxRoleToSelect) &#123;
				return function () &#123;
					ACTION_LOGGER.log("FreCustomCommand select " + box.node.freLanguageConcept() + " box " + self.boxRoleToSelect);
					editor.selectElementBox(selected, self.boxRoleToSelect, self.caretPosition);
				&#125;;
			&#125; else &#123;
				// Default: select the first editable child of the selected element
				return function () &#123;
					ACTION_LOGGER.log("editor.selectFirstEditableChildBox(selected) ");
					editor.selectFirstEditableChildBox(selected);
				&#125;;
			&#125;
		&#125;
		return function(): void &#123;
			if (self.boxRoleToSelect === "REFERENCE") &#123;
				const index = (box.node[box.propertyName] as Array&lt;any&gt;).length -1
				// const empty = editor.findBoxForNode(box.node, box.propertyName)
				editor.selectElement(box.node, box.propertyName, index)
				editor.selectNextLeaf()
				ACTION_LOGGER.log(`REFERENCE node $&#123;box.node.freId()&#125; prop $&#123;box.propertyName&#125; index $&#123;index&#125;`)
			&#125;
		&#125;
		// return EMPTY_POST_ACTION;
	&#125;

&#125;</code></pre>


<PrevNextSection {prevLink} {nextLink} />